import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import ProviderService from '../../services/providerService';
import './ProviderForm.css';

const ProviderForm = () => {
  const { providerId } = useParams();
  const isEdit = useMemo(() => Boolean(providerId), [providerId]);
  const navigate = useNavigate();
  const auth = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    contactName: '',
    phone: '',
    email: '',
    address: '',
    deliveryTimeDays: '',
    servicesInput: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const loadProvider = async () => {
      if (!isEdit) {
        setIsLoading(false);
        return;
      }

      try {
        const token = auth.user?.access_token;
        const provider = await ProviderService.getProviderById(providerId, token);
        if (!provider) {
          setMessage({ type: 'error', text: 'Proveedor no encontrado' });
          setIsLoading(false);
          return;
        }

        setFormData({
          name: provider.name || '',
          contactName: provider.contactName || '',
          phone: provider.phone || '',
          email: provider.email || '',
          address: provider.address || '',
          deliveryTimeDays: provider.deliveryTimeDays ?? '',
          servicesInput: (provider.services || []).join(', '),
          notes: provider.notes || ''
        });
      } catch (error) {
        setMessage({ type: 'error', text: 'Error al cargar el proveedor' });
      } finally {
        setIsLoading(false);
      }
    };

    if (auth.isAuthenticated) {
      loadProvider();
    }
  }, [auth.isAuthenticated, auth.user?.access_token, isEdit, providerId]);

  const parseServicesInput = (value) => {
    if (!value) return [];
    return value
      .split(/[\n,]+/)
      .map(service => service.trim())
      .filter(Boolean);
  };

  const buildPayload = () => {
    const deliveryTime = formData.deliveryTimeDays === ''
      ? null
      : Number(formData.deliveryTimeDays);

    return {
      name: formData.name,
      contactName: formData.contactName,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      deliveryTimeDays: Number.isFinite(deliveryTime) ? deliveryTime : null,
      services: parseServicesInput(formData.servicesInput),
      notes: formData.notes
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBack = () => {
    if (isEdit) {
      navigate(`/laboratory-orders/providers/${providerId}`);
      return;
    }
    navigate('/laboratory-orders/providers');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      const payload = buildPayload();

      if (isEdit) {
        await ProviderService.updateProvider(providerId, payload, token);
        navigate(`/laboratory-orders/providers/${providerId}`, { state: { message: 'Proveedor actualizado exitosamente' } });
      } else {
        const createdProvider = await ProviderService.createProvider(payload, token);
        navigate(`/laboratory-orders/providers/${createdProvider.id}`, { state: { message: 'Proveedor creado exitosamente' } });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: isEdit ? 'Error al actualizar el proveedor' : 'Error al crear el proveedor'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="provider-form-container loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="provider-form-container">
      <div className="provider-form-header">
        <div>
          <h1>{isEdit ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h1>
          <p>Administra la informacion del laboratorio externo</p>
        </div>
        <button className="btn-secondary" onClick={handleBack}>
          Volver
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="provider-form">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="name">
              Nombre del Proveedor <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Ej: Laboratorio Dental XYZ"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contactName">Contacto Principal</label>
            <input
              type="text"
              id="contactName"
              name="contactName"
              value={formData.contactName}
              onChange={handleInputChange}
              placeholder="Nombre del contacto"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Telefono</label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Telefono de contacto"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="correo@laboratorio.com"
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="address">Direccion</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Direccion fisica del laboratorio"
            />
          </div>

          <div className="form-group">
            <label htmlFor="deliveryTimeDays">Tiempo Promedio de Entrega (dias)</label>
            <input
              type="number"
              id="deliveryTimeDays"
              name="deliveryTimeDays"
              value={formData.deliveryTimeDays}
              onChange={handleInputChange}
              min="0"
              placeholder="Ej: 5"
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="servicesInput">Servicios Ofrecidos</label>
            <textarea
              id="servicesInput"
              name="servicesInput"
              value={formData.servicesInput}
              onChange={handleInputChange}
              placeholder="Ej: Protesis fija, Ortodoncia, Impresiones digitales"
            />
            <small className="form-help">
              Puedes separar servicios por comas o saltos de linea.
            </small>
          </div>

          <div className="form-group full-width">
            <label htmlFor="notes">Notas Internas</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Detalles adicionales, politicas de entrega, etc."
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={handleBack}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? <span className="button-spinner" aria-hidden="true"></span> : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProviderForm;
